import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, useTheme, alpha, Paper, Container, Avatar, useMediaQuery } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import StoreIcon from '@mui/icons-material/Store';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import config from './config';

export default function BranchFooter() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedBranch } = useCart();
  const [bgImage, setBgImage] = useState(null);

  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const image = data.find(img => img.Order === 1 && img.IsActive) || data[0];
          if (image) {
            const imgPath = image.ImagePath.startsWith('http')
              ? image.ImagePath
              : `${config.SITE_BASE_URL}${image.ImagePath.startsWith('/') ? '' : '/'}${image.ImagePath}`;
            setBgImage(imgPath);
          }
        }
      })
      .catch(console.error);
  }, []);

  if (!selectedBranch) return null;

  const getBranchImageUrl = () => {
    if (!selectedBranch.ImageUrl && !selectedBranch.Image) return null;
    
    const imageUrl = selectedBranch.ImageUrl || selectedBranch.Image;
    
    if (imageUrl.startsWith('http') || imageUrl.startsWith('data:')) {
      return imageUrl;
    }
    
    return `${config.SITE_BASE_URL}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  const branchImageUrl = getBranchImageUrl();

  return (
    <Container maxWidth="lg" sx={{ my: { xs: 4, sm: 5, md: 6 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper
        elevation={8}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: { xs: 3, sm: 4 },
          bgcolor: bgImage ? 'transparent' : 'primary.main',
          backgroundImage: bgImage ? `url(${bgImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          boxShadow: { xs: '0 8px 24px rgba(0,0,0,0.15)', sm: '0 12px 40px rgba(0,0,0,0.15)' },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: bgImage 
              ? `linear-gradient(135deg, ${alpha('#000', 0.85)} 0%, ${alpha('#000', 0.6)} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            backdropFilter: 'blur(10px)',
            zIndex: 0
          }
        }}
      >
        <Box sx={{ 
          position: 'relative', 
          zIndex: 1, 
          p: { xs: 2.5, sm: 4, md: 5 }, 
          color: 'white' 
        }}>
          {/* Header with Branch Image */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              gap: { xs: 1.5, sm: 2 }, 
              mb: { xs: 3, sm: 4 }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
                flexDirection: isRTL ? 'row-reverse' : 'row',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}
            >
              {branchImageUrl ? (
                <Avatar
                  src={branchImageUrl}
                  alt={selectedBranch.name}
                  sx={{
                    width: { xs: 60, sm: 70, md: 80 },
                    height: { xs: 60, sm: 70, md: 80 },
                    bgcolor: alpha('#fff', 0.2),
                    border: `${isMobile ? '2px' : '3px'} solid ${alpha('#fff', 0.4)}`,
                    boxShadow: { xs: '0 3px 15px rgba(0,0,0,0.3)', sm: '0 4px 20px rgba(0,0,0,0.3)' },
                    '& img': {
                      objectFit: 'cover'
                    }
                  }}
                >
                  <StoreIcon sx={{ fontSize: { xs: 30, sm: 35, md: 40 } }} />
                </Avatar>
              ) : (
                <Box
                  sx={{
                    bgcolor: alpha('#fff', 0.2),
                    borderRadius: '50%',
                    width: { xs: 60, sm: 70, md: 80 },
                    height: { xs: 60, sm: 70, md: 80 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `${isMobile ? '2px' : '3px'} solid ${alpha('#fff', 0.4)}`,
                    boxShadow: { xs: '0 3px 15px rgba(0,0,0,0.3)', sm: '0 4px 20px rgba(0,0,0,0.3)' }
                  }}
                >
                  <StoreIcon sx={{ fontSize: { xs: 30, sm: 35, md: 40 } }} />
                </Box>
              )}
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  textAlign: 'center',
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
                }}
              >
                {selectedBranch.name}
              </Typography>
            </Box>
            
            {/* Working Hours */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600, 
                  opacity: 0.95,
                  textShadow: '0 1px 5px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }
                }}
              >
                {t('branch.twentyFourHours', '24 Hours')}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  opacity: 0.85,
                  textShadow: '0 1px 5px rgba(0,0,0,0.3)',
                  fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' }
                }}
              >
                {t('branch.allDaysOfWeek', 'All days of the week')}
              </Typography>
            </Box>
          </Box>

          {/* Content Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 1fr 1fr' },
              gap: { xs: 2.5, sm: 3, md: 4 },
              textAlign: isRTL ? 'right' : 'left'
            }}
          >
            {/* Address Section */}
            <Box
              sx={{
                bgcolor: alpha('#fff', 0.1),
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                p: { xs: 2, sm: 2.5, md: 3 },
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha('#fff', 0.2)}`,
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: alpha('#fff', 0.15),
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  boxShadow: { xs: '0 4px 16px rgba(0,0,0,0.2)', sm: '0 8px 24px rgba(0,0,0,0.2)' }
                },
                '&:active': {
                  transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)'
                }
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 1.2, sm: 1.5 }, 
                  mb: { xs: 1.5, sm: 2 }, 
                  flexDirection: isRTL ? 'right' : 'left' 
                }}
              >
                <LocationOnIcon sx={{ 
                  fontSize: { xs: 24, sm: 26, md: 28 }, 
                  color: theme.palette.error.light 
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}
                >
                  {t('branch.address', 'Address')}
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  opacity: 0.95, 
                  lineHeight: 1.8,
                  fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
                  wordBreak: 'break-word'
                }}
              >
                {selectedBranch.address || t('common.notAvailable', 'Not available')}
              </Typography>
            </Box>

            {/* Phone Section */}
            <Box
              sx={{
                bgcolor: alpha('#fff', 0.1),
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                p: { xs: 2, sm: 2.5, md: 3 },
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha('#fff', 0.2)}`,
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: alpha('#fff', 0.15),
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  boxShadow: { xs: '0 4px 16px rgba(0,0,0,0.2)', sm: '0 8px 24px rgba(0,0,0,0.2)' }
                },
                '&:active': {
                  transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)'
                }
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 1.2, sm: 1.5 }, 
                  mb: { xs: 1.5, sm: 2 }, 
                  flexDirection: isRTL ? 'right' : 'left' 
                }}
              >
                <PhoneIcon sx={{ 
                  fontSize: { xs: 24, sm: 26, md: 28 }, 
                  color: theme.palette.success.light 
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                  }}
                >
                  {t('branch.phone', 'Phone')}
                </Typography>
              </Box>
              {selectedBranch.PhoneNumber ? (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' }, 
                    opacity: 0.95 
                  }}
                >
                  {selectedBranch.PhoneNumber}
                </Typography>
              ) : (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    opacity: 0.7, 
                    fontStyle: 'italic',
                    fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' }
                  }}
                >
                  {t('common.notAvailable', 'Not available')}
                </Typography>
              )}
            </Box>

            {/* Social Media Section */}
            <Box
              sx={{
                bgcolor: alpha('#fff', 0.1),
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                p: { xs: 2, sm: 2.5, md: 3 },
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha('#fff', 0.2)}`,
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: alpha('#fff', 0.15),
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  boxShadow: { xs: '0 4px 16px rgba(0,0,0,0.2)', sm: '0 8px 24px rgba(0,0,0,0.2)' }
                },
                '&:active': {
                  transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)'
                }
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  mb: { xs: 1.5, sm: 2 },
                  textAlign: isRTL ? 'right' : 'left',
                  fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' }
                }}
              >
                {t('branch.followUs', 'Follow Us')}
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: { xs: 1.5, sm: 2 }, 
                  mb: { xs: 1.5, sm: 2 },
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                  justifyContent: isRTL ? 'flex-end' : 'flex-start'
                }}
              >
                {selectedBranch.facebookUrl && (
                  <IconButton
                    component="a"
                    href={selectedBranch.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      bgcolor: alpha('#3b5998', 0.9),
                      color: 'white',
                      width: { xs: 40, sm: 45, md: 50 },
                      height: { xs: 40, sm: 45, md: 50 },
                      '&:hover': {
                        bgcolor: '#3b5998',
                        transform: 'scale(1.1) rotate(5deg)',
                        boxShadow: '0 8px 20px rgba(59, 89, 152, 0.4)'
                      },
                      '&:active': {
                        transform: isMobile ? 'scale(0.95)' : 'scale(1.1) rotate(5deg)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <FacebookIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 } }} />
                  </IconButton>
                )}
                {selectedBranch.instagramUrl && (
                  <IconButton
                    component="a"
                    href={selectedBranch.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                      color: 'white',
                      width: { xs: 40, sm: 45, md: 50 },
                      height: { xs: 40, sm: 45, md: 50 },
                      '&:hover': {
                        transform: 'scale(1.1) rotate(-5deg)',
                        boxShadow: '0 8px 20px rgba(225, 48, 108, 0.4)'
                      },
                      '&:active': {
                        transform: isMobile ? 'scale(0.95)' : 'scale(1.1) rotate(-5deg)'
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <InstagramIcon sx={{ fontSize: { xs: 22, sm: 26, md: 28 } }} />
                  </IconButton>
                )}
                {!selectedBranch.facebookUrl && !selectedBranch.instagramUrl && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      opacity: 0.7, 
                      fontStyle: 'italic',
                      fontSize: { xs: '0.8rem', sm: '0.875rem' }
                    }}
                  >
                    {t('branch.noSocialMedia', 'No social media links')}
                  </Typography>
                )}
              </Box>
              {selectedBranch.description && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    opacity: 0.85, 
                    lineHeight: 1.6,
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {selectedBranch.description}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
